import { Card } from '@upzi/web-shell/ui/card';
import { Skeleton } from '@upzi/web-shell/ui/skeleton';
import { cn } from '@/lib/utils';
import type { JobResultsViewMode } from './job-results-view-toggle';

const SKELETON_CARD_COUNT = 6;

type JobResultsSkeletonProps = {
  isSplitView: boolean;
  viewMode?: JobResultsViewMode;
};

export function JobResultsSkeleton({
  isSplitView,
  viewMode = 'list',
}: JobResultsSkeletonProps) {
  const isGridView = isSplitView || viewMode === 'grid';

  return (
    <section
      aria-label="Đang tải danh sách việc làm"
      aria-busy="true"
      className="min-w-0"
    >
      <span className="sr-only">Đang tải danh sách việc làm</span>
      <div
        aria-hidden
        className={cn(
          'mb-4 flex h-7 min-w-0 items-center justify-between gap-4',
          isSplitView && 'sm:mb-6'
        )}
      >
        <Skeleton className="bg-surface-variant h-6 w-72 max-w-full motion-reduce:animate-none" />
        {!isSplitView ? (
          <Skeleton className="bg-surface-variant hidden h-7 w-20 shrink-0 rounded-[14px] motion-reduce:animate-none sm:block" />
        ) : null}
      </div>

      <div
        aria-hidden
        className={cn(
          'grid grid-cols-1 gap-3 pb-1',
          !isSplitView && isGridView && 'lg:grid-cols-2'
        )}
      >
        {Array.from({ length: SKELETON_CARD_COUNT }).map((_, index) => (
          <div
            key={index}
            className={cn(
              'h-full min-w-0',
              !isSplitView && !isGridView && 'mx-auto w-full max-w-[896px]'
            )}
          >
            <Card
              className={cn(
                'border-alias-outline bg-surface-pure flex min-h-[168px] w-full flex-col gap-2 overflow-hidden rounded-[18px] border p-0 py-5 shadow-none',
                isGridView && 'h-full min-h-[196px]'
              )}
            >
              <div className="flex w-full items-start gap-3 px-5">
                <Skeleton className="bg-surface-variant size-14 shrink-0 rounded-xl motion-reduce:animate-none" />
                <div className="min-w-0 flex-1">
                  <Skeleton className="bg-surface-variant h-6 w-4/5 motion-reduce:animate-none" />
                  <Skeleton className="bg-surface-variant h-[22px] w-2/5 motion-reduce:animate-none" />
                </div>
                <Skeleton className="bg-surface-variant size-8 shrink-0 rounded-lg motion-reduce:animate-none sm:size-9 sm:rounded-xl" />
              </div>

              {isGridView ? <div className="bg-gray-10 h-px w-full" /> : null}

              <div
                className={cn(
                  'flex w-full flex-1 flex-col gap-1',
                  isGridView ? 'px-5' : 'pr-5 pl-[88px]'
                )}
              >
                <Skeleton className="bg-surface-variant h-[22px] w-3/5 motion-reduce:animate-none" />
                <Skeleton className="bg-surface-variant h-[22px] w-4/5 motion-reduce:animate-none" />
                <Skeleton className="bg-surface-variant h-[22px] w-2/3 motion-reduce:animate-none" />
                <Skeleton
                  className={cn(
                    'bg-surface-variant h-[22px] w-1/2 motion-reduce:animate-none',
                    !isGridView && 'sm:hidden'
                  )}
                />
              </div>
            </Card>
          </div>
        ))}
      </div>
    </section>
  );
}
